import React, { useEffect, useState } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { motion } from "framer-motion";
import { FaArrowRight } from "react-icons/fa";
import { NavLink, useLocation } from "react-router-dom";
import "./productgrid.css";
import Footer from "../../components/footer/footer";
import Header from "../../components/header/header";

const REACT_APP_API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

const Productgrid = () => {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [selectedSubcategory, setSelectedSubcategory] = useState("All");
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const location = useLocation();
  const [selectedBrand, setSelectedBrand] = useState("All");

  const [searchQuery, setSearchQuery] = useState("");

  const disableAnimation = location.pathname === "/shop";

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    setSelectedBrand(params.get("brand") || "All");
    setSelectedCategory(params.get("category") || "All");
    setSelectedSubcategory(params.get("subcategory") || "All");
    setSearchQuery(params.get("search") || "");
  }, [location]);

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${REACT_APP_API_URL}/products`);
        const data = await res.json();

        // ✅ SHOW ALL PRODUCTS
        setProducts(data);
      } catch (err) {
        console.error("Failed to fetch products", err);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  useEffect(() => {
    if (!products.length) return;
    let filtered = [...products];

    if (selectedBrand !== "All") {
      filtered = filtered.filter(p => p.brand?.toLowerCase() === selectedBrand.toLowerCase());
    }

    const uniqueCategories = [...new Set(filtered.map(p => p.category).filter(Boolean))];
    setCategories(["All", ...uniqueCategories]);

    if (selectedCategory !== "All" && !uniqueCategories.includes(selectedCategory)) {
      setSelectedCategory("All");
    }
  }, [products, selectedBrand]);

  useEffect(() => {
    if (!products.length) return;
    let filtered = [...products];

    if (selectedBrand !== "All") {
      filtered = filtered.filter(p => p.brand?.toLowerCase() === selectedBrand.toLowerCase());
    }
    if (selectedCategory !== "All") {
      filtered = filtered.filter(p => p.category === selectedCategory);
    }

    const uniqueSubcategories = [...new Set(filtered.map(p => p.subcategory).filter(Boolean))];
    setSubcategories(["All", ...uniqueSubcategories]);

    if (selectedSubcategory !== "All" && !uniqueSubcategories.includes(selectedSubcategory)) {
      setSelectedSubcategory("All");
    }
  }, [products, selectedBrand, selectedCategory]);

  useEffect(() => {
    let filtered = [...products];

    if (selectedBrand !== "All") {
      filtered = filtered.filter(p => p.brand?.toLowerCase() === selectedBrand.toLowerCase());
    }
    if (selectedCategory !== "All") {
      filtered = filtered.filter(p => p.category === selectedCategory);
    }
    if (selectedSubcategory !== "All") {
      filtered = filtered.filter(p => p.subcategory === selectedSubcategory);
    }
    if (searchQuery !== "") {
      filtered = filtered.filter((p) => p.name?.toLowerCase().includes(searchQuery.toLowerCase()));
    }

    setFilteredProducts(filtered);
  }, [products, selectedBrand, selectedCategory, selectedSubcategory, searchQuery]);

  const getFirstImage = (product) => {
    let rawPath = null;

    if (Array.isArray(product.images) && product.images.length > 0) {
      rawPath = product.images[0];
    } else if (product.images && typeof product.images === 'object') {
      const colorKeys = Object.keys(product.images);
      if (colorKeys.length > 0) {
        const colorImages = product.images[colorKeys[0]];
        rawPath = Array.isArray(colorImages) ? colorImages[0] : colorImages;
      }
    } else if (product.colors && product.colors.length > 0 && product.colors[0].images) {
      rawPath = product.colors[0].images[0] || product.colors[0].images.front;
    }

    if (!rawPath) return "https://placehold.co/400x400/f5f5f7/333?text=No+Image";

    if (rawPath.startsWith("http")) return rawPath;

    const backendUrl = "https://store.crescentworld.com";

    if (rawPath.startsWith("/uploads")) {
      return `${backendUrl}${rawPath}`;
    } else if (rawPath.startsWith("/images")) {
      return rawPath;
    }

    return rawPath;
  };

  const getDisplayPrice = (product) => {
    if (product.variants && product.variants.length > 0) {
      const prices = product.variants.map(v => v.price).filter(Boolean);
      if (prices.length > 0) {
        return Math.min(...prices);
      }
    }
    return product.price || 0;
  };

  const getColorName = (product) => {
    if (product.variants && product.variants.length > 0) {
      const uniqueColors = [...new Set(product.variants.map(v => v.color))];
      return uniqueColors[0] || "";
    }
    if (product.colors && product.colors.length > 0) {
      return product.colors[0].name;
    }
    return "";
  };

  const handleCategoryClick = (category) => {
    setSelectedCategory(category);
    setSelectedSubcategory("All");
    const url = category === "All" ? "/category" : `/category/${category}`;
    window.history.pushState({}, '', url);
  };

  const handleSubcategoryClick = (subcategory) => {
    setSelectedSubcategory(subcategory);
    if (selectedCategory !== "All") {
      if (subcategory === "All") {
        window.history.pushState({}, '', `/category/${selectedCategory}`);
      } else {
        window.history.pushState({}, '', `/category/${selectedCategory}?subcategory=${encodeURIComponent(subcategory)}`);
      }
    }
  };

  if (loading) {
    return (
      <div>
        <Header />
        <Container className="py-5 text-center">
          <div className="loading-spinner">Loading products...</div>
        </Container>
        <Footer />
      </div>
    );
  }

  return (
    <div>
      <Header />

      <Container className="py-5">
        <div className="category-filter-bar mb-3">
          <div className="category-filter-scroll">
            {categories.map((category) => (
              <button
                key={category}
                className={`category-filter-btn ${selectedCategory === category ? 'active' : ''}`}
                onClick={() => handleCategoryClick(category)}
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        {subcategories.length > 1 && (
          <div className="subcategory-filter mb-4">
            <div className="subcategory-scroll">
              {subcategories.map((subcategory) => (
                <button
                  key={subcategory}
                  className={`subcategory-chip ${selectedSubcategory === subcategory ? 'active' : ''}`}
                  onClick={() => handleSubcategoryClick(subcategory)}
                >
                  {subcategory}
                </button>
              ))}
            </div>
          </div>
        )}

        <h2 className="category-title mb-4">
          {selectedBrand !== "All" && (
            <span className=" text-dark text-capitalize">{selectedBrand} </span>
          )}
          {searchQuery ? (
            <>Search Results for "{searchQuery}"</>
          ) : (
            selectedCategory === "All"
              ? "Products"
              : selectedSubcategory === "All"
                ? selectedCategory
                : `${selectedCategory} - ${selectedSubcategory}`
          )}
          <span className="product-count">({filteredProducts.length} products)</span>
        </h2>

        {filteredProducts.length === 0 ? (
           <div className="text-center py-5">
             <h4>No products found.</h4>
             {searchQuery && <p>Try searching for a different keyword.</p>}
           </div>
        ) : (
          <Row className="g-4">
            {filteredProducts.map((item) => {
              const firstImage = getFirstImage(item);
              const displayPrice = getDisplayPrice(item);
              const colorName = getColorName(item);
              const colors = item.colors || [];

              return (
                <Col lg={3} md={6} sm={6} xs={6} key={item._id}>
                  <motion.div
                    className="product-card"
                    whileHover={disableAnimation ? {} : { y: -10 }}
                    transition={disableAnimation ? { duration: 0 } : { duration: 0.3 }}
                  >
                    <motion.img
                      src={firstImage}
                      alt={item.name}
                      className="product-img"
                      whileHover={{ scale: 1.05 }}
                      onError={(e) => {
                        console.log("Image failed to load:", firstImage);
                        e.target.src = "https://placehold.co/400x400/f5f5f7/333?text=No+Image";
                      }}
                    />

                    {colors.length > 0 && (
                      <div className="color-dots">
                        {colors.slice(0, 3).map((color, idx) => (
                          <span
                            key={idx}
                            style={{ backgroundColor: color.hex }}
                            title={color.name}
                          />
                        ))}
                        {colors.length > 3 && (
                          <span className="color-more">+{colors.length - 3}</span>
                        )}
                      </div>
                    )}

                    <h5>
                      {item.name}
                      {colorName && <span className="color-name"> {colorName}</span>}
                    </h5>

                    <p className="starting">STARTING FROM</p>
                    <h4 className="price">₹ {displayPrice.toLocaleString()}</h4>
                    <p className="gst">prices incl. of GST</p>

                    <NavLink to={`/product-details/${item._id}`} style={{ textDecoration: "none" }}>
                      <motion.button
                        className="shop-btn text-dark"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        Shop Now <FaArrowRight />
                      </motion.button>
                    </NavLink>
                  </motion.div>
                </Col>
              );
            })}
          </Row>
        )}
      </Container>
      <Footer />
    </div>
  );
};

export default Productgrid;
